// active-timer.js — visibility-pausable active-time accumulator (Brief 096).
//
// The Lesson Report's "Total time" + per-category bars must reflect ACTIVE time
// on an exercise, not wall-clock. A learner who opens an exercise and walks away
// (tab hidden / screen-locked) for an hour must not see "1 hour on this lesson"
// — that is wrong, demotivating, and would inflate whichever category was
// last-mounted (Brief 096 §Gotchas). So the accumulator pauses while the page is
// hidden and resumes when visible again.
//
// Pure, DOM-free, clock-injected so it's unit-testable with a fake clock. The
// host (kfe-exercise-host) wires it to `performance.now()` + the Visibility API.
//
// Lifecycle: start() on mount, pause()/resume() on visibilitychange, stop() on
// unmount → the flushed active milliseconds get persisted via
// learner-store.addLessonTime.

export class ActiveTimer {
  /** @param {() => number} now monotonic clock in ms (e.g. performance.now) */
  constructor(now) {
    this._now = now;
    /** total active ms already banked from completed running segments */
    this._accumulatedMs = 0;
    /** timestamp the current running segment began, or null when paused/stopped */
    this._runningSince = null;
    this._stopped = false;
  }

  /** Begin (or resume) accruing active time. Idempotent while already running. */
  start() {
    if (this._stopped || this._runningSince != null) return;
    this._runningSince = this._now();
  }

  /** Pause: bank the current running segment and stop the clock. Idempotent. */
  pause() {
    if (this._runningSince == null) return;
    this._accumulatedMs += Math.max(0, this._now() - this._runningSince);
    this._runningSince = null;
  }

  /** Resume after a pause. Alias of start() for call-site readability. */
  resume() {
    this.start();
  }

  /** Active ms so far, including any in-flight running segment (non-mutating). */
  elapsedMs() {
    const live = this._runningSince != null ? Math.max(0, this._now() - this._runningSince) : 0;
    return this._accumulatedMs + live;
  }

  /**
   * Finalise: flush the running segment and freeze. Further start()/resume() are
   * no-ops, so a late visibilitychange after unmount can't re-arm the timer.
   * @returns {number} total active ms
   */
  stop() {
    this.pause();
    this._stopped = true;
    return this._accumulatedMs;
  }
}
